import { UploadOutlined } from "@ant-design/icons";
import { Button, Form, GetProp, Upload, UploadProps } from "antd";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import cn from "classnames";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect, useRef, useState } from "react";
import { Control, Controller, ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import { toast } from "react-toastify";

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

/**
 * Converts field value to Upload fileList format.
 * Handles string URLs (existing files) and UploadFile arrays.
 */
const extractFilename = (url: string): string => {
    try {
        return new URL(url).pathname.split("/").pop() || "file";
    } catch {
        return url.split("/").pop() || "file";
    }
};

const valueToFileList = (value: unknown): UploadFile[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
        return [{
            uid: "-1",
            name: extractFilename(value),
            status: "done",
            url: value,
        }];
    }
    return [];
};

const FILE_MAX_SIZE = 10;

/** Allowed types for regular image uploads. ICO is intentionally excluded — use FAVICON_ACCEPTED_TYPES for favicons. */
export const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif", "image/svg+xml"];

/**
 * Allowed types for favicon uploads.
 * ICO format is not accepted — Intervention Image v3 can't generate ICO output.
 * Browsers accept PNG served at /favicon.ico (content-type matters, not file extension).
 * The backend converts the uploaded image to a 16×16 PNG for serving as favicon.
 */
export const FAVICON_ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif"];

const MIME_TO_EXT: Record<string, string> = {
    "image/jpeg": "jpg/jpeg",
    "image/png": "png",
    "image/webp": "webp",
    "image/avif": "avif",
    "image/svg+xml": "svg",
    "image/x-icon": "ico",
    "image/vnd.microsoft.icon": "ico",
};

const mimeTypesToLabel = (types: string[]): string =>
    [...new Set(types.map((t) => MIME_TO_EXT[t] ?? t))].join(", ");

interface ControlledUploadProps {
    field: ControllerRenderProps<FieldValues, string>;
    baseUploadSettings: UploadProps;
    label: string;
    onFileSelected?: (file: File | null) => void;
    allowRemove?: boolean;
    isRequired?: boolean;
    className?: string;
    size?: SizeType;
    uploadButtonLabel?: string;
    hasError?: boolean;
    errorMessage?: string;
}

/** Separate component so useState is at the top level of a component, not inside a render callback. */
const ControlledUpload: FC<ControlledUploadProps> = ({
    field,
    baseUploadSettings,
    label,
    onFileSelected,
    allowRemove = true,
    isRequired,
    className,
    size = "middle",
    uploadButtonLabel,
    hasError,
    errorMessage,
}) => {
    const { t: tCommon } = usePaldockTranslation("common");
    const [fileList, setFileList] = useState<UploadFile[]>(() => valueToFileList(field.value));
    const didInit = useRef(false);

    useEffect(() => {
        if (!didInit.current && field.value) {
            setFileList(valueToFileList(field.value));
            didInit.current = true;
        }
    }, [field.value]);

    const handleChange = (info: UploadChangeParam) => {
        setFileList(info.fileList);
        if (info.file.status === "removed") {
            onFileSelected?.(null);
        }
        // File selection is handled in beforeUpload, not here
    };

    return (
        <Form.Item
            required={isRequired}
            label={label}
            className={cn(className)}
            validateStatus={hasError ? "error" : undefined}
            help={errorMessage}
        >
            <Upload {...baseUploadSettings} fileList={fileList} onChange={handleChange} maxCount={1} showUploadList={{ showRemoveIcon: allowRemove }}>
                <Button icon={<UploadOutlined />} iconPosition={"end"} size={size}>
                    {uploadButtonLabel || tCommon("upload")}
                </Button>
            </Upload>
        </Form.Item>
    );
};

interface UploadFormItemBaseProps {
    label: string;
    /** Whether to show the remove button on uploaded files. Defaults to true. */
    allowRemove?: boolean;
    /** Only used in the non-controlled (no `control`) path */
    callback?: (uploadParam: UploadChangeParam) => void;
    uploadButtonLabel?: string;
    isRequired?: boolean;
    className?: string;
    /** Button size - "small" | "middle" | "large" */
    size?: SizeType;
    /** Override default accepted MIME types */
    acceptedTypes?: string[];
    /** Minimum image dimensions. File is rejected client-side if smaller. */
    minDimensions?: { width: number; height: number };
}

type UploadFormItemProps<T extends FieldValues = FieldValues> = UploadFormItemBaseProps &
    (
        | {
              /** With react-hook-form integration */
              control: Control<T>;
              name: Path<T>;
              /** Track the File in a ref in the parent and upload on form submit. */
              onFileSelected: (file: File | null) => void;
          }
        | {
              /** Without react-hook-form integration */
              control?: never;
              name: string | Array<string | number>;
              callback: (uploadParam: UploadChangeParam) => void;
              onFileSelected?: never;
          }
    );

const checkImageDimensions = (file: File, minWidth: number, minHeight: number): Promise<boolean> =>
    new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img.naturalWidth >= minWidth && img.naturalHeight >= minHeight);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(true); // let BE validate on error
        };
        img.src = url;
    });

export const UploadFormItem = <T extends FieldValues = FieldValues>({
    label,
    name,
    isRequired,
    className,
    callback,
    onFileSelected,
    allowRemove,
    uploadButtonLabel,
    control,
    size = "middle",
    acceptedTypes,
    minDimensions,
}: UploadFormItemProps<T>) => {
    const { t } = usePaldockTranslation("ui.upload-form-item");
    const { t: tCommon } = usePaldockTranslation("common");

    const allowedTypes = acceptedTypes ?? SUPPORTED_IMAGE_TYPES;

    const beforeUpload = async (file: FileType) => {
        const isSupportedType = allowedTypes.includes(file.type);

        if (!isSupportedType) {
            toast.error(t("error_unsupported_type", { types: mimeTypesToLabel(allowedTypes) }));
            return Upload.LIST_IGNORE;
        }
        const isNotLarge = file.size / 1024 / 1024 < FILE_MAX_SIZE;

        if (!isNotLarge) {
            toast.error(t("error_file_too_large", { size: FILE_MAX_SIZE }));
            return Upload.LIST_IGNORE;
        }

        if (minDimensions) {
            const isLargeEnough = await checkImageDimensions(file as File, minDimensions.width, minDimensions.height);
            if (!isLargeEnough) {
                toast.error(t("error_image_too_small", { width: minDimensions.width, height: minDimensions.height }));
                return Upload.LIST_IGNORE;
            }
        }

        // Capture the file here — this is the only guaranteed moment we have the File object.
        // onChange.originFileObj is unreliable when beforeUpload returns false.
        onFileSelected?.(file as File);

        // Return false to prevent any automatic upload — the form submit will handle it
        return false;
    };

    const baseUploadSettings: UploadProps = {
        name: "file",
        multiple: false,
        accept: allowedTypes.join(","),
        beforeUpload: beforeUpload,
    };

    // If control is provided, use react-hook-form Controller
    if (control) {
        return (
            <Controller
                name={name}
                control={control}
                render={({ field, fieldState }) => (
                    <ControlledUpload
                        field={field as unknown as ControllerRenderProps<FieldValues, string>}
                        baseUploadSettings={baseUploadSettings}
                        label={label}
                        onFileSelected={onFileSelected}
                        allowRemove={allowRemove}
                        isRequired={isRequired}
                        className={className}
                        size={size}
                        uploadButtonLabel={uploadButtonLabel}
                        hasError={!!fieldState.error}
                        errorMessage={fieldState.error?.message}
                    />
                )}
            />
        );
    }

    // Fallback to original antd Form.Item for backwards compatibility
    return (
        <Form.Item required={isRequired} label={label} name={name} className={cn(className)}>
            <Upload {...baseUploadSettings} onChange={callback}>
                <Button icon={<UploadOutlined />} iconPosition={"end"} size={size}>
                    {uploadButtonLabel || tCommon("upload")}
                </Button>
            </Upload>
        </Form.Item>
    );
};
